import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Search as SearchIcon, Loader2, Plus, X } from "lucide-react";
import { apiFetch } from "@/lib/api";
import { NewsCard } from "@/components/NewsCard";
import { CardSkeleton, EmptyState } from "@/components/Skeletons";
import { SEO } from "@/components/SEO";

const PAGE = 20;
const fetchPage = (q, skip) => apiFetch(`/search?q=${encodeURIComponent(q)}&limit=${PAGE}&skip=${skip}`, { auth: false });

function SearchForm({ initial, onSubmit }) {
  const [value, setValue] = useState(initial);
  useEffect(() => setValue(initial), [initial]);
  return (
    <form role="search" data-testid="search-form" onSubmit={(e) => { e.preventDefault(); onSubmit(value.trim()); }}
      className="flex items-center gap-2 bg-surfaceSecondary border border-hair focus-within:border-brand rounded-full pl-4 pr-1.5 py-1.5 transition-colors">
      <SearchIcon size={18} className="text-subtle shrink-0" aria-hidden="true" />
      <input data-testid="search-input" type="search" autoFocus value={value} maxLength={80} onChange={(e) => setValue(e.target.value)}
        placeholder="Cerca notizie, persone, luoghi…" aria-label="Cerca su Readoo"
        className="flex-1 min-w-0 bg-transparent text-base text-onSurface placeholder:text-subtle focus:outline-none [&::-webkit-search-cancel-button]:hidden" />
      {value && (
        <button type="button" data-testid="search-clear" aria-label="Cancella" onClick={() => setValue("")}
          className="w-8 h-8 flex items-center justify-center rounded-full text-subtle hover:text-onSurface"><X size={16} /></button>
      )}
      <button type="submit" data-testid="search-submit" disabled={value.trim().length < 2}
        className="px-5 py-2 rounded-full bg-brand text-onBrand text-sm font-semibold hover:bg-brandSecondary disabled:opacity-50 transition-colors">Cerca</button>
    </form>
  );
}

export default function Search() {
  const [params, setParams] = useSearchParams();
  const q = params.get("q") || "";
  const [items, setItems] = useState([]);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(false);
  const [more, setMore] = useState(false);

  useEffect(() => {
    if (q.length < 2) { setItems([]); setHasMore(false); return undefined; }
    let alive = true;
    setLoading(true);
    fetchPage(q, 0).then((r) => { if (alive) { setItems(r.articles || []); setHasMore(!!r.has_more); } })
      .catch(() => { if (alive) setItems([]); }).finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [q]);

  const loadMore = async () => {
    setMore(true);
    try {
      const r = await fetchPage(q, items.length);
      setItems((prev) => [...prev, ...(r.articles || []).filter((a) => !prev.some((p) => p.article_id === a.article_id))]);
      setHasMore(!!r.has_more);
    } finally { setMore(false); }
  };

  return (
    <div className="max-w-[900px] mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12">
      <SEO title={q ? `Cerca: ${q}` : "Cerca"} description="Cerca tra le notizie di Readoo." />
      <h1 className="font-serif text-4xl sm:text-5xl font-semibold text-onSurface mb-6">Cerca</h1>
      <SearchForm initial={q} onSubmit={(v) => v.length >= 2 && setParams({ q: v })} />
      {q.length >= 2 && !loading && (
        <p data-testid="search-count" className="mt-6 mb-4 font-mono text-xs text-subtle">
          {items.length ? `Risultati per «${q}» · dal più recente` : ""}
        </p>
      )}
      {loading ? (
        <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-5">{Array.from({ length: 4 }).map((_, i) => <CardSkeleton key={i} />)}</div>
      ) : q.length >= 2 && items.length === 0 ? (
        <EmptyState icon={SearchIcon} title={`Nessun articolo per «${q}»`} subtitle="Prova con un'altra parola o controlla come l'hai scritta." />
      ) : (
        <div data-testid="search-results" className="grid grid-cols-1 sm:grid-cols-2 gap-5">
          {items.map((a, i) => <NewsCard key={a.article_id} article={a} index={i} />)}
        </div>
      )}
      {hasMore && !loading && (
        <div className="flex justify-center mt-8">
          <button onClick={loadMore} disabled={more} data-testid="search-load-more"
            className="inline-flex items-center gap-2 px-6 py-3 rounded-full border border-hair bg-surfaceSecondary text-onSurface hover:border-brand/50 hover:text-brand disabled:opacity-60 transition-colors text-sm font-semibold">
            {more ? <><Loader2 size={16} className="animate-spin" /> Caricamento…</> : <><Plus size={16} /> Altri risultati</>}
          </button>
        </div>
      )}
    </div>
  );
}
